/**
 * ML Model Calibration Monitoring UI
 * 
 * Monitors calibration quality of ML models in production.
 * Tracks ECE, MCE, Brier scores, and provides degradation alerts.
 * 
 * Issue: ADMIN-001-A
 */

import React, { useState, useEffect } from 'react';
import { Activity, AlertTriangle, TrendingUp, RefreshCw } from 'lucide-react';
import { getBaseUrl } from '../../../../shared/utils/apiConfig';

interface CalibrationMetric {
  tenant_id: string;
  model_name: string;
  model_version: string;
  ece: number;
  mce: number;
  brier_score: number;
  calibration_status: string;
  quality_assessment: string;
  last_updated: string;
  sample_count: number;
}

interface CalibrationAlert {
  alert_id: string;
  tenant_id: string;
  model_name: string;
  metric_type: string;
  current_value: number;
  threshold: number;
  severity: string;
  message: string;
  created_at: string;
}

interface CalibrationTrend {
  date: string;
  avg_ece: number;
  avg_mce: number;
  avg_brier: number;
  models_monitored: number;
}

const getApiConfig = () => ({
  authToken: localStorage.getItem('astrapeak_auth_token'),
  baseUrl: getBaseUrl()
});

export const CalibrationMonitor: React.FC = () => {
  const [metrics, setMetrics] = useState<CalibrationMetric[]>([]);
  const [alerts, setAlerts] = useState<CalibrationAlert[]>([]);
  const [trends, setTrends] = useState<CalibrationTrend[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTenant, setSelectedTenant] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'metrics' | 'alerts' | 'trends'>('metrics');

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenant]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const { authToken, baseUrl } = getApiConfig();
      
      // Fetch metrics
      const metricsUrl = selectedTenant 
        ? `${baseUrl}/api/platform-admin/ml/calibration/metrics?tenant_id=${selectedTenant}`
        : `${baseUrl}/api/platform-admin/ml/calibration/metrics`;
      
      const metricsRes = await fetch(metricsUrl, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!metricsRes.ok) throw new Error('Failed to fetch metrics');
      const metricsData = await metricsRes.json();
      setMetrics(metricsData.metrics || []);

      // Fetch alerts
      const alertsUrl = selectedTenant
        ? `${baseUrl}/api/platform-admin/ml/calibration/alerts?tenant_id=${selectedTenant}`
        : `${baseUrl}/api/platform-admin/ml/calibration/alerts`;
      
      const alertsRes = await fetch(alertsUrl, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!alertsRes.ok) throw new Error('Failed to fetch alerts');
      const alertsData = await alertsRes.json();
      setAlerts(alertsData.alerts || []);

      // Fetch trends
      const trendsUrl = selectedTenant
        ? `${baseUrl}/api/platform-admin/ml/calibration/trends?tenant_id=${selectedTenant}&days=30`
        : `${baseUrl}/api/platform-admin/ml/calibration/trends?days=30`;
      
      const trendsRes = await fetch(trendsUrl, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!trendsRes.ok) throw new Error('Failed to fetch trends');
      const trendsData = await trendsRes.json();
      setTrends(trendsData.trends || []);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'excellent': return 'text-green-600 bg-green-50';
      case 'good': return 'text-blue-600 bg-blue-50';
      case 'fair': return 'text-yellow-600 bg-yellow-50';
      case 'poor': return 'text-orange-600 bg-orange-50';
      case 'critical': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-red-600 bg-red-50';
      case 'high': return 'text-orange-600 bg-orange-50';
      case 'medium': return 'text-yellow-600 bg-yellow-50';
      case 'low': return 'text-blue-600 bg-blue-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Activity className="w-6 h-6" />
              Calibration Monitor
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Monitor ML model calibration quality and degradation alerts
            </p>
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        <div className="mt-4">
          <input
            type="text"
            placeholder="Filter by Tenant ID (optional)"
            value={selectedTenant}
            onChange={(e) => setSelectedTenant(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg w-full max-w-md"
          />
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      <div className="mb-6 border-b border-gray-200">
        <nav className="flex gap-4">
          {(['metrics', 'alerts', 'trends'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 border-b-2 font-medium ${
                activeTab === tab
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading...</p>
        </div>
      ) : (
        <>
          {activeTab === 'metrics' && (
            <div className="space-y-4">
              {metrics.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No calibration metrics found
                </div>
              ) : (
                metrics.map((metric, idx) => (
                  <div key={idx} className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900">{metric.model_name}</h3>
                        <p className="text-sm text-gray-600">
                          Tenant: {metric.tenant_id} | Version: {metric.model_version}
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(metric.calibration_status)}`}>
                        {metric.calibration_status}
                      </span>
                    </div>
                    
                    <div className="mt-4 grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">ECE</p>
                        <p className="text-lg font-semibold">{metric.ece.toFixed(4)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">MCE</p>
                        <p className="text-lg font-semibold">{metric.mce.toFixed(4)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Brier Score</p>
                        <p className="text-lg font-semibold">{metric.brier_score.toFixed(4)}</p>
                      </div>
                    </div>

                    <div className="mt-4 p-3 bg-gray-50 rounded">
                      <p className="text-sm text-gray-700">{metric.quality_assessment}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Samples: {metric.sample_count} | Updated: {new Date(metric.last_updated).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'alerts' && (
            <div className="space-y-4">
              {alerts.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No calibration alerts
                </div>
              ) : (
                alerts.map((alert) => (
                  <div key={alert.alert_id} className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-orange-600 mt-1" />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-gray-900">{alert.model_name}</h3>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getSeverityColor(alert.severity)}`}>
                            {alert.severity}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{alert.message}</p>
                        <div className="mt-2 text-sm text-gray-500">
                          <span>Metric: {alert.metric_type}</span>
                          <span className="mx-2">|</span>
                          <span>Current: {alert.current_value.toFixed(4)}</span>
                          <span className="mx-2">|</span>
                          <span>Threshold: {alert.threshold.toFixed(4)}</span>
                          <span className="mx-2">|</span>
                          <span>{new Date(alert.created_at).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'trends' && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                30-Day Calibration Trends
              </h3>
              {trends.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No trend data available
                </div>
              ) : (
                <div className="space-y-2">
                  {trends.map((trend, idx) => (
                    <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                      <span className="text-sm text-gray-600">{trend.date}</span>
                      <div className="flex gap-6 text-sm">
                        <span>ECE: {trend.avg_ece.toFixed(4)}</span>
                        <span>MCE: {trend.avg_mce.toFixed(4)}</span>
                        <span>Brier: {trend.avg_brier.toFixed(4)}</span>
                        <span className="text-gray-500">({trend.models_monitored} models)</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};
